import { Form, Input, Select } from 'antd';
import JobDescriptionList from './JobDescriptionList';
import { splitJobDescription } from './jobDescriptionItems';
import useJobPositions, {
  jobPositionLevelShort,
  useJobPositionsT,
} from './useJobPositions';

/**
 * The employee's position. When the company keeps a job position catalog the
 * position is picked from it (`generalData.jobPosition`) and the names in both
 * languages follow the pick — every export and document token reads them.
 * Without a catalog, or with the pick cleared, both stay free text as before.
 * The employee's own education level is never touched here.
 *
 * @param {Object} props
 * @param {Object} props.form - antd form instance of the employee form.
 * @param {string} [props.companyId]
 * @param {Array} props.positionKeys - ['generalData', 'position']
 * @param {Array} props.positionEngKeys - ['generalData', 'positionEng']
 * @param {Object} props.positionState - { visible, disabled }
 * @param {Object} props.positionEngState
 * @param {boolean} [props.hidden] - kept mounted but not shown (form tabs).
 */
export default function JobPositionFormFields({
  form,
  companyId,
  positionKeys,
  positionEngKeys,
  positionState,
  positionEngState,
  hidden = false,
}) {
  const { t, tj } = useJobPositionsT();
  const f = (key) => t(key, { ns: 'forms' });
  const jobPositionKeys = ['generalData', 'jobPosition'];
  const picked = Form.useWatch(jobPositionKeys, form);
  const { data } = useJobPositions(companyId);
  const items = data?.items || [];
  const hasCatalog = items.length > 0;
  // A pick of an archived position (not in the active list) still shows its name.
  const current = picked && !items.some((p) => p._id === picked) ? picked : null;

  const options = [
    ...items.map((p) => ({
      value: p._id,
      label: p.nameSr,
      search: `${p.nameSr} ${p.nameEn || ''} ${p.sectorName || ''}`,
      level: jobPositionLevelShort(p),
      sector: p.sectorName,
    })),
    ...(current
      ? [{ value: current, label: form.getFieldValue(positionKeys) || tj('archivedPosition'), search: '' }]
      : []),
  ];

  const onPick = (value) => {
    const position = items.find((p) => p._id === value);
    if (!position) return;
    form.setFieldValue(positionKeys, position.nameSr);
    if (position.nameEn) form.setFieldValue(positionEngKeys, position.nameEn);
  };

  // Names come from the catalog while a position is picked; an English name
  // the catalog lacks stays typed.
  const fromCatalog = hasCatalog && !!picked;
  const pickedItem = items.find((p) => p._id === picked);
  const engFromCatalog = fromCatalog && !!pickedItem?.nameEn;

  // The picked position's duties, shown under the pick; it follows a new pick
  // at once. An archived pick is not in the active list, so it is looked up
  // among all. A plain employee gets only the position they hold (server).
  const { data: allData } = useJobPositions(
    picked && data && !pickedItem ? companyId : null,
    'all'
  );
  const shownPosition =
    pickedItem || (allData?.items || []).find((p) => p._id === picked);
  const showDescription =
    !!picked && positionState?.visible && !!splitJobDescription(shownPosition?.description).length;
  const level = shownPosition?.educationLevel;
  const levelText =
    level === 'other'
      ? shownPosition?.educationLevelOther || ''
      : level
        ? t(`educationLevels.${level}`, { ns: 'forms' })
        : '';

  return (
    <>
      {hasCatalog && positionState?.visible && (
        <Form.Item
          hidden={hidden}
          name={jobPositionKeys}
          label={tj('employeeField')}
          extra={tj('employeeFieldHint')}
          normalize={(value) => value ?? null}
        >
          <Select
            showSearch
            allowClear
            disabled={positionState?.disabled}
            placeholder={tj('employeeFieldPlaceholder')}
            optionFilterProp="search"
            options={options}
            onChange={onPick}
            optionRender={(option) => (
              <div className="flex items-center justify-between gap-2">
                <span className="truncate">{option.data.label}</span>
                <span className="shrink-0 text-xs text-zinc-400">
                  {[option.data.sector, option.data.level].filter(Boolean).join(' · ')}
                </span>
              </div>
            )}
          />
        </Form.Item>
      )}
      {showDescription && (
        <Form.Item hidden={hidden} label={tj('description')}>
          <div className="text-[13px] leading-relaxed text-zinc-700">
            {levelText && (
              <div className="mb-1">
                <span className="text-zinc-500">{tj('educationLevel')}:</span> {levelText}
              </div>
            )}
            <JobDescriptionList text={shownPosition.description} />
          </div>
        </Form.Item>
      )}
      {positionState?.visible && (
        <Form.Item hidden={hidden} name={positionKeys} label={f('position')}>
          <Input disabled={positionState?.disabled || fromCatalog} placeholder={f('position')} />
        </Form.Item>
      )}
      {positionEngState?.visible && (
        <Form.Item hidden={hidden} name={positionEngKeys} label={f('positionEng')}>
          <Input disabled={positionEngState?.disabled || engFromCatalog} placeholder={f('positionEng')} />
        </Form.Item>
      )}
    </>
  );
}
